import React from 'react';
import { Tooltip, Button } from 'antd';
import { SwapOutlined } from '@ant-design/icons';
import { formatDate, formatDateTime } from '../../../Utils/dateFormat';
import { getAssignedBillerDisplay, getClaimedBillerDisplay } from '../../../Utils/assignedBiller';

const multiSelectFilter = (row, columnId, filterValue) => {
  if (!filterValue || filterValue.length === 0) return true;
  const val = row.getValue(columnId);
  const strVal = val != null ? String(val) : 'N/A';
  return filterValue.includes(strVal);
};

const STATUS_LABELS = {
  INPROGRESS_FOR_DOCTOR: 'In-Progress for Doctor',
  READY_FOR_REVIEWER: 'Ready for Reviewer',
  INPROGRESS_FOR_REVIEWER: 'In-Progress for Reviewer',
  EMR_SUBMITTED: 'EMR Submitted',
  EMR_SUBMITTED_BILLED: 'EMR Submitted Billed',
  EMR_SUBMITTED_NOT_BILLED: 'EMR Submitted Not Billed',
  DELETED: 'Deleted',
  EMR_SUBMISSION_FAILED: 'EMR Submission Failed',
  STRUCK: 'Struck'
};

const formatStatus = value => STATUS_LABELS[value] || value || null;

// Orphaned jobs (billingUnroutable) keep their real status with an "(Orphaned)"
// suffix, e.g. "InProgress(Orphaned)".
const getStatusDisplay = row => {
  const base = formatStatus(row.status);
  if (row.billingUnroutable) {
    return base ? `${base}(Orphaned)` : 'Orphaned';
  }
  return base;
};

const formatBillStatus = value => {
  if (value === true) return 'Billed';
  if (value === false) return 'Unbilled';
  return 'N/A';
};

const billStatusFilterFn = (row, columnId, filterValue) => {
  if (!filterValue || filterValue.length === 0) return true;
  const value = row.getValue(columnId);
  return filterValue.includes(formatBillStatus(value));
};

const formatDateOnly = value => {
  if (!value) return null;
  return formatDate(value);
};

const dateOnlyFilterFn = (row, columnId, filterValue) => {
  if (!filterValue || filterValue.length === 0) return true;
  const value = row.getValue(columnId);
  const formatted = formatDateOnly(value);
  if (!formatted) return filterValue.includes('N/A');
  return filterValue.includes(formatted);
};

const getAgeDays = submissionTime => {
  if (!submissionTime) return null;
  const now = new Date();
  const submitted = new Date(submissionTime);
  return Math.floor((now - submitted) / (1000 * 60 * 60 * 24));
};

export const createBillingAdminColumns = (onReassign) => [
  {
    id: 'jobNumber',
    header: 'Job ID',
    accessorKey: 'jobNumber',
    enableSorting: true,
    filterFn: multiSelectFilter,
    size: 120,
    minSize: 80,
    maxSize: 500,
    cell: ({ getValue }) => getValue() || 'N/A'
  },
  {
    id: 'author',
    header: 'Doctor',
    accessorKey: 'author',
    enableSorting: true,
    filterFn: multiSelectFilter,
    size: 150,
    minSize: 80,
    maxSize: 500,
    cell: ({ getValue }) => {
      const val = getValue();
      if (!val) return 'N/A';
      return <span title={val}>{val.length > 20 ? val.slice(0, 20) + '...' : val}</span>;
    }
  },
  {
    id: 'primaryCarePhysician',
    header: 'Primary Care Physician',
    accessorKey: 'primaryCarePhysician',
    enableSorting: true,
    filterFn: multiSelectFilter,
    size: 210,
    minSize: 80,
    maxSize: 500,
    cell: ({ getValue }) => {
      const val = getValue();
      if (!val) return 'N/A';
      return <span title={val}>{val.length > 20 ? val.slice(0, 20) + '...' : val}</span>;
    }
  },
  {
    id: 'patientName',
    header: 'Patient',
    accessorKey: 'patientName',
    enableSorting: true,
    filterFn: multiSelectFilter,
    size: 150,
    minSize: 80,
    maxSize: 500,
    cell: ({ getValue }) => {
      const val = getValue();
      if (!val) return 'N/A';
      return <span title={val}>{val.length > 20 ? val.slice(0, 20) + '...' : val}</span>;
    }
  },
  {
    id: 'facilityName',
    header: 'Facility',
    accessorFn: row => row.facility?.facilityName,
    enableSorting: true,
    filterFn: multiSelectFilter,
    size: 200,
    minSize: 100,
    maxSize: 500,
    cell: ({ getValue }) => getValue() || 'N/A'
  },
  {
    id: 'assignedBillers',
    header: 'Assigned Biller',
    accessorFn: row => getAssignedBillerDisplay(row) || undefined,
    enableSorting: false,
    filterFn: multiSelectFilter,
    size: 180,
    minSize: 100,
    maxSize: 500,
    cell: ({ getValue }) => getValue() || 'N/A'
  },
  {
    id: 'claimedBiller',
    header: 'Claimed Biller',
    accessorFn: row => getClaimedBillerDisplay(row) || undefined,
    enableSorting: true,
    filterFn: multiSelectFilter,
    size: 160,
    minSize: 100,
    maxSize: 500,
    cell: ({ getValue }) => getValue() || 'Unclaimed'
  },
  {
    id: 'workType',
    header: 'Work Type',
    accessorKey: 'workType',
    enableSorting: true,
    filterFn: multiSelectFilter,
    size: 150,
    minSize: 100,
    maxSize: 500,
    cell: ({ getValue }) => getValue() || 'N/A'
  },
  {
    id: 'dictationTemplate',
    header: 'Dictation Template',
    accessorFn: row => {
      const name = row.dictationTemplate?.templateName;
      return name === 'None' ? null : name;
    },
    enableSorting: true,
    filterFn: multiSelectFilter,
    size: 180,
    minSize: 120,
    maxSize: 500,
    cell: ({ getValue }) => getValue() || 'N/A'
  },
  {
    id: 'currentAssignee',
    header: 'Current Assignee',
    accessorKey: 'currentAssignee',
    enableSorting: true,
    filterFn: multiSelectFilter,
    size: 180,
    minSize: 100,
    maxSize: 500,
    cell: ({ getValue }) => getValue() || 'N/A'
  },
  {
    id: 'status',
    header: 'Status',
    accessorFn: row => getStatusDisplay(row),
    enableSorting: true,
    filterFn: multiSelectFilter,
    size: 150,
    minSize: 80,
    maxSize: 500,
    meta: {
      getUniqueValues: rows => {
        const vals = new Set();
        rows.forEach(r => {
          const label = getStatusDisplay(r.original);
          if (label) vals.add(label);
        });
        return Array.from(vals).sort();
      }
    },
    cell: ({ getValue }) => getValue() || 'N/A'
  },
  {
    id: 'billStatus',
    header: 'Bill Status',
    accessorKey: 'billStatus',
    enableSorting: false,
    filterFn: billStatusFilterFn,
    size: 110,
    minSize: 80,
    maxSize: 200,
    meta: {
      getUniqueValues: rows => {
        const values = new Set();
        rows.forEach(row => values.add(formatBillStatus(row.original.billStatus)));
        return Array.from(values).sort();
      }
    },
    cell: ({ getValue }) => {
      const val = getValue();
      if (val === true) return <span style={{ color: '#52c41a' }}>Billed</span>;
      if (val === false) return <span style={{ color: '#47A4EA' }}>Unbilled</span>;
      return 'N/A';
    }
  },
  {
    id: 'effectiveDate',
    header: 'Encounter Date',
    accessorKey: 'effectiveDate',
    enableSorting: true,
    filterFn: dateOnlyFilterFn,
    size: 160,
    minSize: 100,
    maxSize: 500,
    meta: {
      isDateColumn: true,
      getUniqueValues: rows => {
        const dates = new Set();
        rows.forEach(row => {
          const formatted = formatDateOnly(row.original.effectiveDate);
          dates.add(formatted || 'N/A');
        });
        return Array.from(dates).sort();
      }
    },
    cell: ({ getValue }) => {
      const value = getValue();
      if (!value) return 'N/A';
      return formatDate(value);
    }
  },
  {
    id: 'submittedTime',
    header: 'Submitted Time',
    accessorKey: 'submissionTime',
    enableSorting: true,
    filterFn: dateOnlyFilterFn,
    size: 180,
    minSize: 120,
    maxSize: 500,
    meta: {
      isDateColumn: true,
      getUniqueValues: rows => {
        const dates = new Set();
        rows.forEach(row => {
          const formatted = formatDateOnly(row.original.submissionTime);
          dates.add(formatted || 'N/A');
        });
        return Array.from(dates).sort();
      }
    },
    cell: ({ getValue }) => {
      const value = getValue();
      if (!value) return 'N/A';
      return formatDateTime(value);
    }
  },
  {
    id: 'emrSubmissionTime',
    header: 'EMR Submission Time',
    accessorKey: 'emrSubmissionTime',
    enableSorting: true,
    filterFn: dateOnlyFilterFn,
    size: 220,
    minSize: 120,
    maxSize: 500,
    meta: {
      isDateColumn: true,
      getUniqueValues: rows => {
        const dates = new Set();
        rows.forEach(row => {
          const formatted = formatDateOnly(row.original.emrSubmissionTime);
          dates.add(formatted || 'N/A');
        });
        return Array.from(dates).sort();
      }
    },
    cell: ({ getValue }) => {
      const value = getValue();
      if (!value) return 'N/A';
      return formatDateTime(value);
    }
  },
  {
    id: 'submissionToReviewerLevel1Time',
    header: 'Submission Time to Reviewer',
    accessorKey: 'submissionToReviewerLevel1Time',
    enableSorting: true,
    filterFn: dateOnlyFilterFn,
    size: 280,
    minSize: 150,
    maxSize: 500,
    meta: {
      isDateColumn: true,
      getUniqueValues: rows => {
        const dates = new Set();
        rows.forEach(row => {
          const formatted = formatDateOnly(row.original.submissionToReviewerLevel1Time);
          dates.add(formatted || 'N/A');
        });
        return Array.from(dates).sort();
      }
    },
    cell: ({ getValue }) => {
      const value = getValue();
      if (!value) return 'N/A';
      return formatDateTime(value);
    }
  },
  {
    id: 'rejectedByReviewerTime',
    header: 'Reviewer Rejected Time',
    accessorKey: 'rejectedByReviewerTime',
    enableSorting: true,
    filterFn: dateOnlyFilterFn,
    size: 240,
    minSize: 150,
    maxSize: 500,
    meta: {
      isDateColumn: true,
      getUniqueValues: rows => {
        const dates = new Set();
        rows.forEach(row => {
          const formatted = formatDateOnly(row.original.rejectedByReviewerTime);
          dates.add(formatted || 'N/A');
        });
        return Array.from(dates).sort();
      }
    },
    cell: ({ getValue }) => {
      const value = getValue();
      if (!value) return 'N/A';
      return formatDateTime(value);
    }
  },
  {
    id: 'billedTime',
    header: 'Billed Time',
    accessorKey: 'billedTime',
    enableSorting: true,
    filterFn: dateOnlyFilterFn,
    size: 180,
    minSize: 120,
    maxSize: 500,
    meta: {
      isDateColumn: true,
      getUniqueValues: rows => {
        const dates = new Set();
        rows.forEach(row => {
          const formatted = formatDateOnly(row.original.billedTime);
          dates.add(formatted || 'N/A');
        });
        return Array.from(dates).sort();
      }
    },
    cell: ({ getValue }) => {
      const value = getValue();
      if (!value) return 'N/A';
      return formatDateTime(value);
    }
  },
  {
    id: 'ageInQueue',
    header: 'Age in Queue',
    accessorKey: 'submissionTime',
    enableSorting: true,
    enableColumnFilter: false,
    sortingFn: 'datetime',
    size: 120,
    minSize: 80,
    maxSize: 200,
    cell: ({ getValue }) => {
      const days = getAgeDays(getValue());
      if (days === null) return 'N/A';
      return (
        <span style={{ color: days > 7 ? '#ff4d4f' : 'inherit', fontWeight: days > 7 ? 600 : 400 }}>
          {days}d
        </span>
      );
    }
  },
  {
    id: 'admissionDate',
    header: 'Admission Date',
    accessorFn: row => {
      const date = row.patientDemographics?.admissionDate;
      if (!date) return null;
      return formatDate(date);
    },
    enableSorting: true,
    sortingFn: (rowA, rowB) => {
      const a = rowA.original.patientDemographics?.admissionDate;
      const b = rowB.original.patientDemographics?.admissionDate;
      if (!a && !b) return 0;
      if (!a) return 1;
      if (!b) return -1;
      return new Date(a).getTime() - new Date(b).getTime();
    },
    filterFn: multiSelectFilter,
    size: 160,
    minSize: 100,
    maxSize: 500,
    meta: { isDateColumn: true },
    cell: ({ getValue }) => getValue() || 'N/A'
  },
  {
    id: 'mrn',
    header: 'MRN',
    accessorFn: row => row.patientDemographics?.medicalRecordNumber,
    enableSorting: true,
    filterFn: multiSelectFilter,
    size: 150,
    minSize: 80,
    maxSize: 500,
    cell: ({ getValue }) => getValue() || 'N/A'
  },
  {
    id: 'dischargeDate',
    header: 'Discharge Date',
    accessorFn: row => {
      const date = row.patientDemographics?.dischargeDate;
      if (!date) return null;
      return formatDate(date);
    },
    enableSorting: true,
    sortingFn: (rowA, rowB) => {
      const a = rowA.original.patientDemographics?.dischargeDate;
      const b = rowB.original.patientDemographics?.dischargeDate;
      if (!a && !b) return 0;
      if (!a) return 1;
      if (!b) return -1;
      return new Date(a).getTime() - new Date(b).getTime();
    },
    filterFn: multiSelectFilter,
    size: 160,
    minSize: 100,
    maxSize: 500,
    meta: { isDateColumn: true },
    cell: ({ getValue }) => getValue() || 'N/A'
  },
  ...(onReassign ? [{
    id: 'actions',
    header: '',
    size: 60,
    minSize: 60,
    enableResizing: false,
    enableSorting: false,
    enableColumnFilter: false,
    cell: ({ row }) => (
      <Tooltip title='Reassign job'>
        <Button
          type='text'
          icon={<SwapOutlined />}
          onClick={() => onReassign(row.original)}
        />
      </Tooltip>
    )
  }] : [])
];
